import {
  Award,
  FileBarChart,
  FileSpreadsheet,
  History,
  PieChart,
  Users,
} from "lucide-react";
import ExcelJS from "exceljs";
import { useState } from "react";
import { useApp } from "../../domain/AppContext";
import { compareClassNames, formatClassName } from "../../domain/classCode";
import { translateEnrollmentStatus, translatePaymentStatus } from "../../domain/reportFormatting";
import { editionYear, reportTable } from "../../domain/reportData";
import { Button, Dialog, EmptyState, Field, PageHeader } from "../../components/ui";

const reports = [
  {
    id: "participation",
    title: "Participação por olimpíada",
    description: "Pessoas inscritas, confirmadas e pendentes por competição.",
    formats: ["PDF", "Excel"],
    icon: PieChart,
  },
  {
    id: "student-history",
    title: "Histórico por estudante",
    description: "Trajetória completa de um estudante em olimpíadas.",
    formats: ["PDF", "Excel"],
    icon: History,
  },
  {
    id: "class-participation",
    title: "Participação por turma",
    description: "Adesão por turma e série, com comparativo.",
    formats: ["PDF", "Excel"],
    icon: Users,
  },
  {
    id: "awards",
    title: "Medalhas e classificações",
    description: "Quadro de premiações por estudante e competição.",
    formats: ["PDF", "Excel"],
    icon: Award,
  },
  {
    id: "enrollment-status",
    title: "Status das inscrições",
    description: "Situação de cada inscrição e pendências abertas.",
    formats: ["PDF", "Excel"],
    icon: FileSpreadsheet,
  },
  {
    id: "fiscal",
    title: "Relatório fiscal de inscrições",
    description: "Taxa única do colégio e cobranças por estudante, equipe e fase, sem duplicar integrantes.",
    formats: ["PDF", "Excel"],
    icon: FileSpreadsheet,
  },
  {
    id: "annual",
    title: "Resumo anual de olimpíadas",
    description: "Documento institucional para direção e conselho.",
    formats: ["PDF", "Excel"],
    icon: FileBarChart,
  },
];

export function ReportsPage() {
  const { profile, data, notify } = useApp();
  const [active, setActive] = useState<(typeof reports)[number] | null>(null);
  const [format, setFormat] = useState("PDF");
  const [generating, setGenerating] = useState(false);
  const [className, setClassName] = useState('all');
  const [year, setYear] = useState<number | null>(null);
  const [studentId, setStudentId] = useState('');
  const [olympiadId, setOlympiadId] = useState('all');
  if (!data) return null;
  const selectedYear = year ?? data.institution.schoolYear;
  const years = [...new Set([data.institution.schoolYear, ...data.olympiads.map(editionYear), ...data.enrollments.map((e) => Number(e.requestedAt.slice(0, 4))), ...data.enrollments.flatMap((e) => (e.payments ?? []).map((p) => Number((p.dueDate || e.requestedAt).slice(0, 4))))])].filter(Number.isFinite).sort((a, b) => b - a);
  const generate = async () => {
    if (!active) return;
    if (!profile?.unitId || (active.id === 'student-history' && !studentId)) {
      notify({ type: 'error', title: 'Selecione o estudante para gerar seu histórico.' }); return;
    }
    setGenerating(true);
    try {
    const table = reportTable(active.id, data, { year: selectedYear, unitId: profile.unitId, className, olympiadId, studentId });
    const generatedAt = new Date();
    const generatedLabel = generatedAt.toLocaleString('pt-BR');
    const escapeHtml = (value: unknown) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char));
    if (format === 'PDF') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) throw new Error('Permita a abertura da janela de impressão e tente novamente.');
      if (printWindow) {
        printWindow.opener = null;
        const headerHtml = `<tr>${table[0].map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>`;
        const bodyHtml = table.slice(1).map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('');
        printWindow.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(active.title)}</title><style>
          @page{size:A4 landscape;margin:18mm 14mm 16mm;@bottom-center{content:"Página " counter(page) " de " counter(pages);font:10px Arial;color:#64728b}}
          *{box-sizing:border-box}body{margin:0;padding:0 0 18mm;font-family:Arial,Helvetica,sans-serif;color:#172033;background:#fff;font-size:11px}.report-header{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding-bottom:18px;border-bottom:3px solid #1c4793;margin-bottom:20px}.report-header img{width:235px;height:auto;object-fit:contain}.report-meta{text-align:right;color:#64728b;line-height:1.5}.report-meta strong{display:block;color:#172033;font-size:12px}.report-title{margin:0 0 5px;color:#1c4793;font-size:25px;line-height:1.15}.report-context{margin:0 0 18px;color:#64728b;font-size:11px}.report-table{width:100%;border-collapse:separate;border-spacing:0;border:1px solid #d9e1ee;border-radius:10px;overflow:hidden}.report-table th{padding:9px 8px;background:#1c4793;color:#fff;text-align:left;font-size:10px;letter-spacing:.02em}.report-table td{padding:8px;border-top:1px solid #e6ebf3;color:#27344a;font-size:10px;vertical-align:top}.report-table tr:nth-child(even) td{background:#f5f8fc}.report-footer{position:fixed;left:0;right:0;bottom:0;border-top:1px solid #d9e1ee;padding-top:7px;color:#64728b;font-size:9px;display:flex;justify-content:space-between}.report-footer span:last-child:after{content:"Página " counter(page) " de " counter(pages)}
          @media print{.report-table{page-break-inside:auto}.report-table tr{page-break-inside:avoid;page-break-after:auto}.report-header{page-break-after:avoid}}
        </style></head><body><header class="report-header"><img src="${window.location.origin}/brand/oficina-sapiens-logo-horizontal-univap.png" alt="Oficina Sapiens · Colégios Univap"><div class="report-meta"><strong>Extraído por ${escapeHtml(profile?.name ?? 'Usuário autenticado')}</strong><span>${escapeHtml(generatedLabel)}</span><span>${escapeHtml(data.institution.activeUnit)} · Ano letivo ${escapeHtml(selectedYear)}</span></div></header><h1 class="report-title">${escapeHtml(active.title)}</h1><p class="report-context">Relatório institucional · dados conforme o recorte selecionado no sistema</p><table class="report-table"><thead>${headerHtml}</thead><tbody>${bodyHtml}</tbody></table><footer class="report-footer"><span>Oficina Sapiens · Colégios Univap</span><span></span></footer><script>window.addEventListener('load',()=>window.print())<\/script></body></html>`);
        printWindow.document.close();
      }
    } else {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = profile?.name ?? 'Oficina Sapiens';
      workbook.created = generatedAt;
      const sheet = workbook.addWorksheet(active.title.slice(0, 31));
      sheet.addRow(['Oficina Sapiens · Colégios Univap']);
      sheet.addRow([active.title]);
      sheet.addRow([`Extraído por: ${profile?.name ?? 'Usuário autenticado'}`, `Data e hora: ${generatedLabel}`, `Unidade: ${data.institution.activeUnit}`, `Ano letivo: ${selectedYear}`]);
      sheet.addRow([]);
      const headerRow = sheet.addRow(table[0]);
      headerRow.eachCell((cell) => { cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1C4793' } }; cell.alignment = { vertical: 'middle' }; });
      table.slice(1).forEach((row) => sheet.addRow(row));
      sheet.views = [{ state: 'frozen', ySplit: 5 }];
      sheet.columns.forEach((column) => { const values = column.values ?? []; column.width = Math.min(42, Math.max(14, ...values.slice(1).map((value) => String(value ?? '').length + 2))); });
      const bytes = await workbook.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${active.id}-oficina-sapiens.xlsx`; anchor.click(); URL.revokeObjectURL(url);
    }
    setGenerating(false);
    setActive(null);
    notify({
      type: "success",
      title: format === 'PDF' ? "Relatório aberto para impressão em PDF" : "Relatório gerado",
      description: `${table.length - 1} linha(s) no recorte de ${selectedYear}. Cada relatório declara sua unidade de contagem.`,
    });
    } catch (error) { notify({ type: 'error', title: 'Relatório não gerado', description: error instanceof Error ? error.message : 'Tente novamente.' }); }
    finally { setGenerating(false); }
  };
  return (
    <div className="reports-page">
      <PageHeader
        eyebrow="Exportações institucionais"
        title="Central de relatórios"
        description="Cada relatório declara a pergunta que responde e oferece uma prévia antes de gerar."
      />
      <section className="report-grid">
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <article key={report.id}>
              <span>
                <Icon size={22} />
              </span>
              <h2>{report.title}</h2>
              <p>{report.description}</p>
              <div>
                {report.formats.map((item) => (
                  <em key={item}>{item}</em>
                ))}
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setActive(report);
                  setFormat(report.formats[0]);
                }}
              >
                Gerar relatório
              </Button>
            </article>
          );
        })}
      </section>
      <section className="recent-reports">
        <h2>Gerados recentemente</h2>
        <EmptyState icon={<History />} title="Nenhum relatório gerado" description="Os relatórios gerados aparecerão aqui." />
      </section>
      <Dialog
        open={Boolean(active)}
        title={active?.title ?? "Gerar relatório"}
        description="Defina o recorte antes de gerar."
        onClose={() => setActive(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setActive(null)}>
              Cancelar
            </Button>
            <Button loading={generating} onClick={generate}>
              Gerar relatório
            </Button>
          </>
        }
      >
        <div className="dialog-form">
          <div className="form-grid two">
            <Field label="Ano letivo">
              <select value={selectedYear} onChange={(event) => setYear(Number(event.target.value))}>{years.map((value) => <option key={value} value={value}>{value}</option>)}</select>
            </Field>
            <Field label="Unidade">
              <select disabled><option>{data.institution.activeUnit}</option></select>
            </Field>
            <Field label="Turma">
              <select value={className} onChange={(event) => setClassName(event.target.value)}>
                <option value="all">Todas</option>
                {[...new Set(data.students.map((item) => item.className))].sort(compareClassNames).map((item) => <option key={item} value={item}>{formatClassName(item)}</option>)}
              </select>
            </Field>
            <Field label="Olimpíada">
              <select value={olympiadId} onChange={(event) => setOlympiadId(event.target.value)}>
                <option value="all">Todas</option>
                {data.olympiads.map((item) => (
                  <option key={item.id} value={item.id}>{item.acronym} {editionYear(item)}</option>
                ))}
              </select>
            </Field>
          </div>
          {active?.id === 'student-history' ? <Field label="Estudante"><select value={studentId} onChange={(event) => setStudentId(event.target.value)}><option value="">Selecione</option>{data.students.filter((s) => className === 'all' || s.className === className).map((s) => <option key={s.id} value={s.id}>{s.name} · {formatClassName(s.className)}</option>)}</select></Field> : null}
          <p>Pessoas: cada integrante ocupa sua própria linha. Fiscal: cada parcela de equipe aparece uma vez; a taxa do colégio é institucional. Presença não é inferida de inscrição.</p>
          {active && profile?.unitId ? <p aria-live="polite">Prévia: {reportTable(active.id, data, { year: selectedYear, unitId: profile.unitId, className, olympiadId, studentId }).length - 1} linha(s) no recorte.</p> : null}
          <Field label="Formato">
            <div className="choice-chips">
              {active?.formats.map((item) => (
                <button
                  type="button"
                  key={item}
                  className={format === item ? "active" : ""}
                  onClick={() => setFormat(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </Field>
          <div className="report-preview">
            <strong>Prévia do recorte</strong>
            <p>
              {olympiadId === 'all' ? data.olympiads.length : 1} olimpíada(s) · {data.enrollments.filter((enrollment) => { const student = data.students.find((item) => enrollment.studentIds.includes(item.id)); return (className === 'all' || student?.className === className) && (olympiadId === 'all' || enrollment.olympiadId === olympiadId); }).length}{" "}{active?.id === 'fiscal' ? 'inscrição(ões) usada(s) para consolidar as cobranças sem duplicar equipes.' : 'inscrição(ões) no recorte.'}
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
